import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    featureFlagAdminApi,
    type FeatureFlagRules,
    type FeatureFlagTargetRule,
    type ManagedFeatureFlag,
} from '@/api/feature-flag-admin';
import { Edit, Flag, Add, Refresh, Floppy, Trash } from '@/components/icons';
import {
    AppDialogLayout,
    EmptyState,
    ListSection,
    PageHeader,
    PageShell,
} from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import Loading from '@/components/ui/Loading';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { FEATURE_FLAGS_QUERY_KEY } from '@/hooks/use-feature-flags';
import { cn } from '@/lib/utils';

const FEATURE_FLAG_ADMIN_STATUS_QUERY_KEY = ['feature-flags', 'admin', 'me'] as const;
const FEATURE_FLAG_ADMIN_LIST_QUERY_KEY = ['feature-flags', 'admin'] as const;

const PLAN_OPTIONS = [
    { value: 'TRIAL', label: 'Trial' },
    { value: 'BASIC', label: 'Basic' },
    { value: 'INTERMEDIATE', label: 'Intermediário' },
    { value: 'PRO', label: 'Pro' },
] as const;

const STATUS_OPTIONS = [
    { value: 'active', label: 'Ativa' },
    { value: 'trialing', label: 'Trial Stripe' },
    { value: 'trial', label: 'Trial' },
    { value: 'trial_not_started', label: 'Trial não iniciado' },
    { value: 'trial_expired', label: 'Trial expirado' },
    { value: 'past_due', label: 'Pagamento pendente' },
    { value: 'canceled', label: 'Cancelada' },
    { value: 'unpaid', label: 'Inadimplente' },
    { value: 'incomplete', label: 'Incompleta' },
    { value: 'incomplete_expired', label: 'Incompleta expirada' },
] as const;

type TargetRuleForm = {
    id: string;
    emailDomains: string;
    userEmails: string;
    percentage: string;
    environments: string[];
    plans: string[];
    statuses: string[];
};

type FeatureFlagFormState = {
    enabled: boolean;
    defaultEnabled: boolean;
    rules: TargetRuleForm[];
};

const createRuleId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const createEmptyRule = (): TargetRuleForm => ({
    id: createRuleId(),
    emailDomains: '',
    userEmails: '',
    percentage: '',
    environments: [],
    plans: [],
    statuses: [],
});

const splitList = (value: string) =>
    value
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);

const joinList = (value?: string[]) => value?.join(', ') ?? '';

function createInitialFormState(flag: ManagedFeatureFlag): FeatureFlagFormState {
    return {
        enabled: flag.enabled,
        defaultEnabled: flag.default_enabled,
        rules: flag.rules.any.map((rule) => ({
            id: createRuleId(),
            emailDomains: joinList(rule.emailDomains),
            userEmails: joinList(rule.userEmails ?? rule.userIds),
            percentage: rule.percentage === undefined ? '' : String(rule.percentage),
            environments: rule.environments ?? [],
            plans: rule.plans ?? [],
            statuses: rule.statuses ?? [],
        })),
    };
}

function hasRuleCriteria(rule: TargetRuleForm) {
    return (
        splitList(rule.emailDomains).length > 0 ||
        splitList(rule.userEmails).length > 0 ||
        rule.environments.length > 0 ||
        rule.plans.length > 0 ||
        rule.statuses.length > 0 ||
        rule.percentage.trim() !== ''
    );
}

function toFeatureFlagRules(formRules: TargetRuleForm[]): FeatureFlagRules {
    const any = formRules.map((rule): FeatureFlagTargetRule => {
        const nextRule: FeatureFlagTargetRule = {};
        const emailDomains = splitList(rule.emailDomains).map((domain) =>
            domain.replace(/^@/, '').toLowerCase(),
        );
        const userEmails = splitList(rule.userEmails).map((email) => email.toLowerCase());

        if (emailDomains.length) nextRule.emailDomains = emailDomains;
        if (userEmails.length) nextRule.userEmails = userEmails;
        if (rule.environments.length) {
            nextRule.environments = rule.environments as FeatureFlagTargetRule['environments'];
        }
        if (rule.plans.length) nextRule.plans = rule.plans;
        if (rule.statuses.length) nextRule.statuses = rule.statuses;

        if (rule.percentage.trim() !== '') {
            const percentage = Number(rule.percentage);
            if (!Number.isInteger(percentage) || percentage < 0 || percentage > 100) {
                throw new Error('invalid_percentage');
            }

            nextRule.percentage = percentage;
        }

        return nextRule;
    });

    if (formRules.some((rule) => !hasRuleCriteria(rule))) {
        throw new Error('empty_rule');
    }

    return { any };
}

function summarizeRule(rule: FeatureFlagTargetRule) {
    const parts: string[] = [];

    if (rule.emailDomains?.length) {
        parts.push(rule.emailDomains.map((domain) => `@${domain}`).join(', '));
    }
    if (rule.userEmails?.length) {
        parts.push(`${rule.userEmails.length} e-mail(s)`);
    }
    if (rule.userIds?.length) parts.push(`${rule.userIds.length} usuário(s)`);
    if (rule.environments?.length) parts.push(rule.environments.join(', '));
    if (rule.plans?.length) parts.push(rule.plans.join(', '));
    if (rule.statuses?.length) parts.push(`${rule.statuses.length} status`);
    if (rule.percentage !== undefined) parts.push(`${rule.percentage}% rollout`);

    return parts.length ? parts.join(' + ') : 'Todos os usuários';
}

function summarizeRules(rules: FeatureFlagRules) {
    if (!rules.any.length) return 'Sem targeting';

    const summaries = rules.any.slice(0, 2).map(summarizeRule);
    const remaining = rules.any.length - summaries.length;

    return remaining > 0 ? `${summaries.join(' ou ')} +${remaining}` : summaries.join(' ou ');
}

function formatDate(value: string | null) {
    if (!value) return '-';

    return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
}

function toggleListValue(values: string[], value: string) {
    return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function ToggleButton({
    active,
    onClick,
    label,
    description,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    description: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'rounded-xl border px-4 py-3 text-left transition-colors',
                active
                    ? 'border-brand/25 bg-brand-subtle text-brand'
                    : 'border-border bg-background text-foreground hover:bg-surface-muted',
            )}
        >
            <span className="block text-sm font-semibold">{label}</span>
            <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                {description}
            </span>
        </button>
    );
}

function OptionChips({
    options,
    values,
    onChange,
}: {
    options: ReadonlyArray<{ value: string; label: string }>;
    values: string[];
    onChange: (values: string[]) => void;
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((option) => {
                const active = values.includes(option.value);

                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(toggleListValue(values, option.value))}
                        className={cn(
                            'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                            active
                                ? 'border-brand/25 bg-brand-subtle text-brand'
                                : 'border-border bg-background text-muted-foreground hover:bg-surface-muted',
                        )}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

function TargetRuleEditor({
    rule,
    index,
    onChange,
    onRemove,
}: {
    rule: TargetRuleForm;
    index: number;
    onChange: (rule: TargetRuleForm) => void;
    onRemove: () => void;
}) {
    const updateField = (patch: Partial<TargetRuleForm>) => {
        onChange({ ...rule, ...patch });
    };

    return (
        <div className="rounded-2xl border border-border/80 bg-surface-muted/40 p-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Regra {index + 1}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Todos os critérios preenchidos nesta regra precisam bater.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRemove}
                    className="text-destructive hover:text-destructive"
                >
                    <Trash className="size-4" />
                    Remover
                </Button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="space-y-2">
                    <span className="text-xs font-medium text-foreground">Domínios de e-mail</span>
                    <Input
                        value={rule.emailDomains}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            updateField({ emailDomains: event.target.value })
                        }
                        placeholder="laudopsi.com, cliente.com"
                    />
                </label>
                <label className="space-y-2">
                    <span className="text-xs font-medium text-foreground">Rollout percentual</span>
                    <Input
                        value={rule.percentage}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            updateField({ percentage: event.target.value })
                        }
                        inputMode="numeric"
                        placeholder="0 a 100"
                    />
                </label>
            </div>

            <div className="mt-4 space-y-4">
                <div className="space-y-2">
                    <span className="text-xs font-medium text-foreground">Planos</span>
                    <OptionChips
                        options={PLAN_OPTIONS}
                        values={rule.plans}
                        onChange={(plans) => updateField({ plans })}
                    />
                </div>
                <details className="rounded-xl border border-border/70 bg-background px-4 py-3">
                    <summary className="cursor-pointer text-xs font-semibold text-foreground">
                        Avançado
                    </summary>
                    <div className="mt-4 space-y-4">
                        <label className="space-y-2">
                            <span className="text-xs font-medium text-foreground">
                                E-mails de usuários
                            </span>
                            <Input
                                value={rule.userEmails}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    updateField({ userEmails: event.target.value })
                                }
                                placeholder="ana@clinica.com, suporte@laudopsi.com"
                            />
                        </label>
                        <div className="space-y-2">
                            <span className="text-xs font-medium text-foreground">Status</span>
                            <OptionChips
                                options={STATUS_OPTIONS}
                                values={rule.statuses}
                                onChange={(statuses) => updateField({ statuses })}
                            />
                        </div>
                    </div>
                </details>
            </div>
        </div>
    );
}

function FeatureFlagEditorDialog({
    flag,
    open,
    onOpenChange,
}: {
    flag: ManagedFeatureFlag | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const queryClient = useQueryClient();
    const [form, setForm] = useState<FeatureFlagFormState | null>(null);

    useEffect(() => {
        if (!flag || !open) return;
        setForm(createInitialFormState(flag));
    }, [flag, open]);

    const updateMutation = useMutation({
        mutationFn: () => {
            if (!flag || !form) throw new Error('missing_flag_form');

            return featureFlagAdminApi.updateFlag(flag.key, {
                enabled: form.enabled,
                default_enabled: form.defaultEnabled,
                rules: toFeatureFlagRules(form.rules),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: FEATURE_FLAG_ADMIN_LIST_QUERY_KEY,
            });
            queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
            toast.success('Feature flag atualizada.');
            onOpenChange(false);
        },
        onError: (error) => {
            if (error instanceof Error && error.message === 'invalid_percentage') {
                toast.error('O rollout percentual precisa ser um número inteiro entre 0 e 100.');
                return;
            }
            if (error instanceof Error && error.message === 'empty_rule') {
                toast.error('Remova regras vazias ou preencha pelo menos um critério.');
                return;
            }

            toast.error('Não foi possível atualizar a feature flag.');
        },
    });

    if (!flag || !form) return null;

    const updateRule = (ruleId: string, nextRule: TargetRuleForm) => {
        setForm((current) =>
            current
                ? {
                      ...current,
                      rules: current.rules.map((rule) => (rule.id === ruleId ? nextRule : rule)),
                  }
                : current,
        );
    };

    const removeRule = (ruleId: string) => {
        setForm((current) =>
            current
                ? {
                      ...current,
                      rules: current.rules.filter((rule) => rule.id !== ruleId),
                  }
                : current,
        );
    };

    const hasChanges =
        form.enabled !== flag.enabled ||
        form.defaultEnabled !== flag.default_enabled ||
        JSON.stringify(toComparableRules(form.rules)) !== JSON.stringify(flag.rules);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[calc(100vh-2rem)] min-h-0 max-w-5xl flex-col overflow-hidden p-0">
                <AppDialogLayout
                    title="Editar feature flag"
                    description={flag.key}
                    icon={<Flag className="size-5" />}
                    bodyClassName="space-y-6"
                    footer={
                        <DialogFooter className="gap-2 sm:space-x-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="button"
                                onClick={() => updateMutation.mutate()}
                                disabled={!hasChanges || updateMutation.isPending}
                            >
                                <Floppy className="size-4" />
                                Salvar alterações
                            </Button>
                        </DialogFooter>
                    }
                >
                    <div className="grid gap-3 md:grid-cols-2">
                        <ToggleButton
                            active={form.enabled}
                            onClick={() =>
                                setForm((current) =>
                                    current ? { ...current, enabled: !current.enabled } : current,
                                )
                            }
                            label="Flag operacional ativa"
                            description="Quando desligada, a flag nega acesso mesmo que as regras casem."
                        />
                        <ToggleButton
                            active={form.defaultEnabled}
                            onClick={() =>
                                setForm((current) =>
                                    current
                                        ? {
                                              ...current,
                                              defaultEnabled: !current.defaultEnabled,
                                          }
                                        : current,
                                )
                            }
                            label="Default global ligado"
                            description="Quando ligado, usuários sem regra específica recebem a feature."
                        />
                    </div>

                    <section className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-base font-semibold text-foreground">
                                    Regras de targeting
                                </h2>
                                <p className="mt-1 text-sm text-muted-foreground">
                                    A feature será liberada se qualquer regra abaixo bater.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    setForm((current) =>
                                        current
                                            ? {
                                                  ...current,
                                                  rules: [...current.rules, createEmptyRule()],
                                              }
                                            : current,
                                    )
                                }
                            >
                                <Add className="size-4" />
                                Adicionar regra
                            </Button>
                        </div>

                        {!flag.rules_valid ? (
                            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                As regras salvas estão inválidas. Salvar este modal substituirá o
                                targeting inválido pelas regras configuradas aqui.
                            </div>
                        ) : null}

                        {form.rules.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-border bg-surface-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
                                Nenhuma regra configurada. Use o default global ou adicione
                                targeting específico.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {form.rules.map((rule, index) => (
                                    <TargetRuleEditor
                                        key={rule.id}
                                        rule={rule}
                                        index={index}
                                        onChange={(nextRule) => updateRule(rule.id, nextRule)}
                                        onRemove={() => removeRule(rule.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                </AppDialogLayout>
            </DialogContent>
        </Dialog>
    );
}

function toComparableRules(formRules: TargetRuleForm[]) {
    try {
        return toFeatureFlagRules(formRules);
    } catch {
        return { any: [] };
    }
}

function FeatureFlagsTable({
    flags,
    onEdit,
}: {
    flags: ManagedFeatureFlag[];
    onEdit: (flag: ManagedFeatureFlag) => void;
}) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Flag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead>Targeting</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Atualizada</TableHead>
                    <TableHead className="w-24 text-right">Ações</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {flags.map((flag) => (
                    <TableRow key={flag.key}>
                        <TableCell className="min-w-72">
                            <div className="space-y-1">
                                <div className="font-medium text-foreground">{flag.key}</div>
                                <div className="line-clamp-2 text-xs text-muted-foreground">
                                    {flag.description}
                                </div>
                            </div>
                        </TableCell>
                        <TableCell>
                            <div className="flex flex-wrap gap-2">
                                <Badge variant={flag.enabled ? 'success' : 'neutral'}>
                                    {flag.enabled ? 'Ativa' : 'Off'}
                                </Badge>
                                {!flag.rules_valid ? (
                                    <Badge variant="destructive">Regras inválidas</Badge>
                                ) : null}
                            </div>
                        </TableCell>
                        <TableCell>
                            <Badge variant={flag.default_enabled ? 'warning' : 'neutral'}>
                                {flag.default_enabled ? 'Ligado' : 'Off'}
                            </Badge>
                        </TableCell>
                        <TableCell className="max-w-80 text-sm text-muted-foreground">
                            {summarizeRules(flag.rules)}
                        </TableCell>
                        <TableCell>
                            <Badge variant={flag.expose_to_client ? 'info' : 'neutral'}>
                                {flag.expose_to_client ? 'Exposta' : 'Backend'}
                            </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(flag.updated_at)}
                        </TableCell>
                        <TableCell className="text-right">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => onEdit(flag)}
                            >
                                <Edit className="size-4" />
                                Editar
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default function FeatureFlagsPage() {
    const [selectedFlag, setSelectedFlag] = useState<ManagedFeatureFlag | null>(null);
    const { data: adminStatus, isLoading: isLoadingAdminStatus } = useQuery({
        queryKey: FEATURE_FLAG_ADMIN_STATUS_QUERY_KEY,
        queryFn: featureFlagAdminApi.getAdminStatus,
        retry: false,
    });
    const isAdmin = adminStatus?.is_admin === true;
    const {
        data: flags = [],
        isLoading: isLoadingFlags,
        refetch: refetchFlags,
    } = useQuery({
        queryKey: FEATURE_FLAG_ADMIN_LIST_QUERY_KEY,
        queryFn: featureFlagAdminApi.listFlags,
        enabled: isAdmin,
        retry: false,
    });

    if (isLoadingAdminStatus) {
        return <Loading />;
    }

    if (!isAdmin) {
        return <Navigate to="/" replace />;
    }

    return (
        <PageShell width="wide">
            <PageHeader
                eyebrow="Operações internas"
                title="Feature flags"
                subtitle="POC para controlar rollout de features registradas. A autorização e a validação final acontecem no backend."
                icon={Flag}
                action={
                    <Button type="button" variant="outline" onClick={() => refetchFlags()}>
                        <Refresh className="size-4" />
                        Atualizar
                    </Button>
                }
            />

            <ListSection
                title="Flags registradas"
                description="Somente chaves conhecidas pelo registry podem ser alteradas."
            >
                {isLoadingFlags ? (
                    <Loading className="h-auto py-8" size={120} />
                ) : flags.length === 0 ? (
                    <EmptyState
                        icon={Flag}
                        title="Nenhuma flag registrada"
                        description="Adicione chaves no registry do backend para gerenciá-las aqui."
                        className="border-0 shadow-none"
                    />
                ) : (
                    <FeatureFlagsTable flags={flags} onEdit={setSelectedFlag} />
                )}
            </ListSection>

            <FeatureFlagEditorDialog
                flag={selectedFlag}
                open={Boolean(selectedFlag)}
                onOpenChange={(open) => {
                    if (!open) setSelectedFlag(null);
                }}
            />
        </PageShell>
    );
}
